// Main container for the onboarding wizard flow
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { CheckCircle2, Building2, CreditCard, HelpCircle } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { BrandWizardStep } from './BrandWizardStep'
import { HelpWizardStep } from './HelpWizardStep'
import { PlanSelector } from '../billing/PlanSelector'
import { BrandPreview } from '../brand/BrandPreview'
import { Database } from '@/database.types'
import fieldMetadata from '@/lib/fieldMetadata.json'
import { cn } from "@/lib/utils"
import { useAnalytics } from '@/lib/analytics'
import { useBrands } from '@/lib/BrandsProvider'

type OnboardingStep = 'help' | 'brand' | 'plan' | 'success'
type Brand = Database['public']['Tables']['brands']['Row']

const STEP_PROGRESS = {
  help: 0,
  brand: 33,
  plan: 66,
  success: 100,
} as const

const STEP_PROGRESS_NO_BILLING = {
  help: 0,
  brand: 50,
  success: 100,
} as const

const STEP_ORDER: OnboardingStep[] = ['help', 'brand', 'plan', 'success']

// Get key fields for brand preview
const keyFields = Object.entries(fieldMetadata)
  .filter(([_, meta]) => meta.keyField && !meta.hidden)
  .sort((a, b) => a[1].group.localeCompare(b[1].group))

export function WizardLayout() {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('help')
  const [loading, setLoading] = useState(true)
  const [existingBrandId, setExistingBrandId] = useState<string | null>(null)
  const [brandData, setBrandData] = useState<Partial<Brand>>({})
  const navigate = useNavigate()
  const { trackEvent, events } = useAnalytics()
  const [searchParams] = useSearchParams()
  const skipBilling = searchParams.get('skipBilling') === 'true'
  const urlBrandId = searchParams.get('brandId')
  const { setSelectedBrand } = useBrands()

  // Add auto-redirect effect for success step when skipBilling is true
  useEffect(() => {
    if (currentStep === 'success' && skipBilling) {
      console.log('Success step reached with skipBilling=true, preparing to redirect');
      const timer = setTimeout(async () => {
        console.log('Executing redirect to purchase success page');
        // Track onboarding completion
        trackEvent(events.USER_ONBOARDING_COMPLETED, {
          brand_id: brandData.id,
          brand_name: brandData.brand_name,
          time_to_complete: new Date().getTime() - (new Date(brandData.created_at || Date.now()).getTime()),
          industry: brandData.brand_inspiration
        });
        
        // Update onboarding status before redirecting
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          try {
            await supabase
              .schema('marketing_and_customer_success')
              .from('profiles')
              .update({ onboarding_completed: true })
              .eq('id', session.user.id);
            console.log('Onboarding completed flag set to true (auto-redirect)');
          } catch (error) {
            console.error('Error updating onboarding status:', error);
          }
        }
        
        navigate('/settings/purchase-success?skipBilling=true');
      }, 1000); // Short delay to ensure state updates are complete
      return () => clearTimeout(timer);
    }
  }, [currentStep, skipBilling, brandData, navigate, trackEvent]);

      useEffect(() => {
        const loadOnboardingState = async () => {
            console.log('loadOnboardingState called. skipBilling:', skipBilling, 'urlBrandId:', urlBrandId);
            
            // Check if we need to set the current step from URL parameter
            const stepParam = searchParams.get('step')
            const isBrandStep = stepParam === 'brand'
            if (stepParam && STEP_ORDER.includes(stepParam as OnboardingStep)) {
                setCurrentStep(stepParam as OnboardingStep)
            }

            // Step detection for URL brandId is handled below in the brandId section
      
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        navigate('/login')
        return
      }

      // If a brandId is provided in the URL, use it for preselected brand context
      // Do NOT force skip to the brand step; start on the first step (help)
      if (urlBrandId) {
        console.log('[WizardLayout] 🎯 Brand ID found in URL:', urlBrandId)
        console.log('[WizardLayout] skipBilling mode:', skipBilling)
        console.log('[WizardLayout] Using provided brand ID without skipping initial step')
        setExistingBrandId(urlBrandId)
        
        // Fetch the brand data to display in the preview
        const { data: brandData, error } = await supabase
          .from('brands')
          .select('*')
          .eq('id', urlBrandId)
          .single()
          
        if (error) {
          console.error('[WizardLayout] ❌ Error fetching claimed brand from URL:', error)
          // Don't return here - still set loading to false
        } else if (brandData) {
          console.log('[WizardLayout] ✅ Successfully loaded claimed brand data:', {
            id: brandData.id,
            name: brandData.brand_name,
            status: brandData.status,
            url: brandData.url,
            elevator_pitch: brandData.elevator_pitch?.substring(0, 50) + '...',
            target_market: brandData.target_market?.substring(0, 50) + '...',
            web_site_scraping_status: brandData.web_site_scraping_status
          })
          
          // DEBUG: Log all key fields that should be displayed
          console.log('[DEBUG] Key fields in loaded brand:');
          keyFields.forEach(([fieldName]) => {
            const value = brandData[fieldName as keyof Brand];
            if (value) {
              console.log(`  - ${fieldName}: ${String(value).substring(0, 50)}${String(value).length > 50 ? '...' : ''}`);
            }
          });
          
          setBrandData(brandData)
        } else {
          console.warn('[WizardLayout] ⚠️ No brand data returned for URL brand ID:', urlBrandId)
        }
        
        setLoading(false)
        return
      }

      // Only check onboarding status if we're not skipping billing and no brandId in URL
      if (!skipBilling) {
        // See if the user has completed onboarding
        const { data: onboardingStatus } = await supabase
          .schema('marketing_and_customer_success')
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', session.user.id)
          .limit(1)
          .single()

        console.log('onboardingStatus', onboardingStatus)

        if (onboardingStatus?.onboarding_completed && !isBrandStep) {
          console.log('[WizardLayout] User has completed onboarding and no brandId in URL, redirecting to dashboard')
          navigate('/dashboard')
          return
        } else if (onboardingStatus?.onboarding_completed && isBrandStep) {
          console.log('[WizardLayout Debug] Onboarding completed but user needs to create a brand')
          // Allow the user to go through brand creation even though onboarding is completed
          setCurrentStep('brand')
        }
      }

      // Otherwise, check for draft brand
      const { data: allBrands } = await supabase
        .from('brands')
        .select('*')
        .eq('account_id', session.user.id);
        
      console.log('Found brands for current user:', allBrands?.map(b => ({
        id: b.id, 
        status: b.status, 
        scrape_status: b.web_site_scraping_status,
        name: b.brand_name
      })));

      if (allBrands?.length) {
        // First priority: brand with completed web scraping
        const scrapedBrand = allBrands.find(b => b.web_site_scraping_status === 'COMPLETED');
        
        // Second priority: DRAFT brand
        const draftBrand = allBrands.find(b => b.status === 'DRAFT');
        
        // Third priority: most recently updated brand
        const sortedBrands = [...allBrands].sort((a, b) => 
          new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime()
        );
        
        // Select the brand based on priority
        const selectedBrand = scrapedBrand || draftBrand || sortedBrands[0];
        
        console.log('Selected brand based on priority:', selectedBrand.id, 
          scrapedBrand ? '(web-scraped)' : draftBrand ? '(draft)' : '(most recent)');
          
        setExistingBrandId(selectedBrand.id);
        setBrandData(selectedBrand);
      } else {
        console.log('No brands found');
      }

      setLoading(false)
    }

    loadOnboardingState()
  }, [skipBilling, urlBrandId])

  const updateStep = async (step: OnboardingStep) => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return

    // Get current step index
    const currentIndex = STEP_ORDER.indexOf(currentStep)
    const targetIndex = STEP_ORDER.indexOf(step)

    // Track step change
    trackEvent(events.FEATURE_USED, {
      feature: 'onboarding_step_change',
      previous_step: currentStep,
      next_step: step,
      direction: targetIndex > currentIndex ? 'forward' : 'backward',
      user_id: session.user.id
    })

    // Only allow moving to the next step or any previous step
    if (targetIndex > currentIndex + 1) {
      console.log('Cannot skip steps')
      trackEvent(events.ERROR_OCCURRED, {
        error_type: 'step_skip_attempt',
        current_step: currentStep,
        attempted_step: step
      })
      return
    }

    // Only validate brand status when moving forward
    if (targetIndex > currentIndex) {
      // If moving from brand step to plan step, verify brand exists and is active
      if (currentStep === 'brand' && step === 'plan') {
        const { data: brands } = await supabase
          .from('brands')
          .select('*')
          .eq('status', 'ACTIVE')
          .limit(1)

        if (!brands?.length) {
          console.error('No active brand found. Please complete brand setup first.')
          trackEvent(events.ERROR_OCCURRED, {
            error_type: 'missing_active_brand',
            current_step: currentStep,
            attempted_step: step
          })
          return
        }

        // Update brand data
        setBrandData(brands[0])
        
        // Track brand completion
        trackEvent(events.BRAND_CREATED, {
          brand_id: brands[0].id,
          brand_name: brands[0].brand_name,
          industry: brands[0].brand_inspiration
        })

        // If we're skipping billing, go straight to success
        if (skipBilling) {
          setCurrentStep('success')
          return
        }
      }

      // If we've reached success, verify all previous steps
      if (step === 'success') {
        const { data: brands } = await supabase
          .from('brands')
          .select('*')
          .eq('status', 'ACTIVE')
          .limit(1)

        if (!brands?.length) {
          console.error('No active brand found')
          trackEvent(events.ERROR_OCCURRED, {
            error_type: 'missing_active_brand_at_success',
            current_step: currentStep
          })
          return
        }

        // Update brand data
        setBrandData(brands[0])
      }
    }

    setCurrentStep(step)
  }

  const handleStepComplete = (step: OnboardingStep) => {
    console.log(`Step ${step} completed. skipBilling:`, skipBilling);
    
    const nextStep = STEP_ORDER[STEP_ORDER.indexOf(step) + 1]
    console.log(`Next step should be: ${nextStep}`);
    
    // Check if we're in post-onboarding brand creation mode
    const stepParam = searchParams.get('step')
    const isPostOnboardingBrandCreation = stepParam === 'brand'
    
    // If user completes brand creation in post-onboarding mode, ensure brand is active and redirect to dashboard
    if (isPostOnboardingBrandCreation && step === 'brand') {
      console.log('[WizardLayout Debug] Post-onboarding brand creation completed, ensuring brand is active')
      
      // Ensure the brand is activated and onboarding is marked complete before redirecting
      if (brandData?.id) {
        ensureBrandActive(brandData.id as string)
          .then(async () => {
            console.log('[WizardLayout Debug] Brand activated, ensuring onboarding is marked complete')
            
            // Also ensure onboarding is marked as complete in case it wasn't already
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              try {
                await supabase
                  .schema('marketing_and_customer_success')
                  .from('profiles')
                  .update({ onboarding_completed: true })
                  .eq('id', session.user.id);
                console.log('[WizardLayout Debug] Ensured onboarding_completed is true');
              } catch (error) {
                console.error('[WizardLayout Debug] Error updating onboarding status:', error);
              }
            }
            
            console.log('[WizardLayout Debug] Redirecting to dashboard')
            navigate('/dashboard')
          })
          .catch((error) => {
            console.error('[WizardLayout Debug] Failed to activate brand:', error)
            // Still redirect to dashboard even if activation failed
            navigate('/dashboard')
          })
      } else {
        console.log('[WizardLayout Debug] No brand ID available, redirecting to dashboard anyway')
        navigate('/dashboard')
      }
      return
    }
    
    // If we're completing the brand step and skipBilling is true,
    // we need to ensure we move to success
    if (skipBilling && step === 'brand') {
      console.log('🔍 DEBUG: skipBilling=true and brand step completed - will trigger convergence processing');
      console.log('Moving to success step immediately');
      
      // Move to success step immediately
      setCurrentStep('success');
      
      // Try to process brand in the background
      if (brandData.id) {
        console.log('🔍 DEBUG: Processing brand in background:', brandData.id);
        // Fire and forget - don't block the UI
        setTimeout(() => {
          console.log('🔍 DEBUG: Starting background process for brand:', brandData.id);
          ensureBrandActive(brandData.id as string)
            .then(() => {
              console.log('🔍 DEBUG: Brand activated successfully, now triggering convergence processing');
              if (brandData.id) { // Recheck id before use
                console.log('🔍 DEBUG: Triggering brand processing because skipBilling=true');
                triggerBrandProcessing(brandData.id as string)
                  .catch(err => {
                    console.error('🔍 DEBUG: Brand processing failed, but continuing onboarding:', err);
                  });
              }
            })
            .catch(err => {
              console.error('🔍 DEBUG: Failed to set brand as active, but continuing onboarding:', err);
            });
        }, 100);
      } else {
        console.error('🔍 DEBUG: No brand ID available for processing');
      }
      return;
    } else if (step === 'brand') {
      console.log('🔍 DEBUG: Brand step completed but skipBilling=false - NOT triggering convergence processing');
    }
    
    // For non-skipBilling flow or other steps
    if (nextStep) {
      updateStep(nextStep);
    }
  }

  // Function to ensure brand is set to ACTIVE status
  const ensureBrandActive = async (brandId: string) => {
    try {
      console.log('Ensuring brand is active:', brandId);
      const { data, error } = await supabase
        .from('brands')
        .update({ 
          status: 'ACTIVE',
          updated_at: new Date().toISOString()
        })
        .eq('id', brandId)
        .select()
        .single();
        
      if (error) {
        console.error('Failed to update brand status:', error);
      } else {
        console.log('Brand status updated to ACTIVE');
        // Update local brandData
        setBrandData(prev => ({ ...prev, status: 'ACTIVE' }));
        
        // Update the brand in the BrandsProvider context
        if (data) {
          setSelectedBrand(data as Brand);
        }
      }
    } catch (error) {
      console.error('Error updating brand status:', error);
    }
  }

  // Function to trigger brand convergence processing
  const triggerBrandProcessing = async (brandId: string): Promise<void> => {
    try {
      console.log('🔍 DEBUG: Triggering brand processing for brand:', brandId);
      console.log('🔍 DEBUG: Current step:', currentStep, 'skipBilling:', skipBilling);
      console.log('🔍 DEBUG: Brand data:', JSON.stringify(brandData));
      
      // Get the current session to include auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.error('🔍 DEBUG: No active session found when trying to trigger brand processing');
        return;
      }
      
      console.log('🔍 DEBUG: Session user ID:', session.user.id);
      
      // Create a promise that rejects after 5 seconds
      const timeoutPromise = new Promise<Response>((_, reject) => {
        setTimeout(() => reject(new Error('Brand processing request timed out')), 5000);
      });
      
      const apiEndpoint = `${import.meta.env.VITE_WORKERS_URL}/rainfactory/brand-genius/process_updated_brand`;
      console.log('🔍 DEBUG: Calling API endpoint:', apiEndpoint);
      
      // Create the fetch promise - removing x-user-id header to avoid CORS issues
      const fetchPromise = fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
          // Removed x-user-id header to avoid CORS issues
        },
        body: JSON.stringify({
          brandId,
          userId: session.user.id, // Including user ID in the body instead
        })
      });
      
      // Race the fetch against the timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]) as Response;
      
      if (!response.ok) {
        console.error('🔍 DEBUG: API response not OK:', response.status, response.statusText);
        throw new Error(`Failed to trigger brand processing: ${response.statusText}`);
      } else {
        const responseData = await response.json();
        console.log('🔍 DEBUG: Brand processing response:', JSON.stringify(responseData));
        console.log('🔍 DEBUG: Brand processing triggered successfully');
      }
    } catch (error) {
      console.error('🔍 DEBUG: Error triggering brand processing:', error);
      throw error; // Re-throw so it can be caught by the caller
    }
  }

  const handleBrandUpdate = (data: Partial<Brand>) => {
    console.log('Brand data update received:', data.id ? `ID: ${data.id}` : 'No ID');
    
    // DEBUG: Log full data being received
    console.log('[DEBUG handleBrandUpdate] Full data received:', {
      id: data.id,
      brand_name: data.brand_name,
      elevator_pitch: data.elevator_pitch?.substring(0, 50) + '...',
      url: data.url,
      keyCount: Object.keys(data).length
    });
    
    // CRITICAL FIX: Add debugging to track field edits in text inputs
    if (!data.id) {
      console.log('Field update contains these properties:', Object.keys(data).join(', '));
    }
    
    // Skip updates for simple form field edits in HelpWizardStep to prevent circular updates
    // Check if we're in HelpWizardStep and this is just a text field edit update
    if (currentStep === 'help' && 
        data.id === undefined && 
        Object.keys(data).some(key => 
          ['brand_name', 'brand_description', 'product_description', 'target_audience'].includes(key)
        )) {
      console.log('Skipping form field update from HelpWizardStep to prevent circular updates');
      // Only update brandData without changing existingBrandId
      setBrandData(prev => ({ ...prev, ...data }));
      return;
    }
    
    // Also catch form field edits in BrandWizardStep
    if (currentStep === 'brand' && 
        data.id === undefined && 
        !data.status && // Status changes are significant and should be processed
        Object.keys(data).every(key => 
          ['brand_name', 'brand_description', 'product_description', 'target_audience', 
          'competitors', 'customer_challenges', 'unique_value_proposition', 
          'brand_story', 'brand_mission', 'brand_values'].includes(key)
        )) {
      console.log('Handling form field update from BrandWizardStep - updating preview only');
      // Only update brandData without changing existingBrandId
      setBrandData(prev => ({ ...prev, ...data }));
      return;
    }
    
    // If this is a brand with web_site_scraping_status, log that information
    if (data.web_site_scraping_status) {
      console.log('Received brand update with scraping status:', data.web_site_scraping_status);
    }
    
    setBrandData(prev => ({ ...prev, ...data }))
    
    // If the brand ID is included in the update, set it as the existingBrandId
    // This ensures we don't create a new brand when transitioning from HelpWizardStep to BrandWizardStep
    if (data.id) {
      console.log('Setting existingBrandId from brand update:', data.id)
      
      // Check if this is a different brand ID than we already have
      if (existingBrandId && existingBrandId !== data.id) {
        console.log('WARNING: Brand ID changed from', existingBrandId, 'to', data.id);
        console.log('Current step:', currentStep, 'skipBilling:', skipBilling);
        
        // If the current existingBrandId is from a web-scraped brand, prioritize it
        if (brandData.web_site_scraping_status === 'COMPLETED') {
          console.log('Current brand has completed web scraping - preserving it');
          return;
        }
        
        // In most cases when a different brand ID appears in a brand update, we should 
        // trust the new one (e.g., from web scraping or other processes)
        
        // However, in skipBilling mode, prioritize the URL brand ID over a newly created one
        if (skipBilling && urlBrandId && urlBrandId !== data.id) {
          console.log('In skipBilling mode - prioritizing URL brand ID:', urlBrandId);
          console.log('Ignoring new brand update with ID:', data.id);
          // Don't return, but use urlBrandId instead of data.id
          setExistingBrandId(urlBrandId);
          return;
        }
      }
      
      setExistingBrandId(data.id)
      
      // When skipBilling is true, also update localStorage so the brand stays selected
      // This ensures the brand remains selected after onboarding
      if (skipBilling && data.id) {
        console.log('Setting selected brand in localStorage:', data.id);
        localStorage.setItem('selectedBrandId', data.id);
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-lg opacity-40 animate-pulse" />
          <div className="relative bg-white rounded-xl p-8 shadow-xl">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl animate-spin" style={{ animationDuration: '3s' }} />
              <p className="text-gray-600 font-medium">Loading your experience...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Subtle animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000" />
        <div className="absolute top-40 left-40 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000" />
      </div>

      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>
      
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 min-h-screen relative z-10">
        <div className="flex flex-col lg:grid lg:grid-cols-[45%_55%] gap-6 max-w-7xl mx-auto">
          {/* Left side: Wizard */}
          <div>
            <Card className="relative bg-white/95 backdrop-blur-xl shadow-xl border border-purple-100/50 rounded-2xl overflow-hidden">
              {/* Top gradient accent */}
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400" />

              <div className="p-6 sm:p-8">
              <Tabs value={currentStep} className="w-full">
                <ScrollArea>
                  <TabsList className={cn(
                    "grid w-full mb-4 bg-gray-50/50 p-1 rounded-xl",
                    skipBilling ? "grid-cols-3" : "grid-cols-4"
                  )}>
                    <TabsTrigger
                      value="help"
                      disabled={currentStep !== 'help' && currentStep === 'success'}
                      onClick={() => updateStep('help')}
                      className="group relative rounded-lg transition-all duration-200 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-purple-700 py-2.5"
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <HelpCircle
                          className="opacity-60 group-data-[state=active]:opacity-100 group-data-[state=active]:text-purple-600"
                          size={16}
                          strokeWidth={2}
                        />
                        {currentStep === 'help' && <span className="hidden sm:inline font-medium">Welcome</span>}
                        <Badge
                          className={cn(
                            "ml-1.5 transition-all",
                            currentStep === 'help'
                              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0'
                              : 'bg-gray-100 text-gray-600'
                          )}
                        >
                          1
                        </Badge>
                      </div>
                    </TabsTrigger>
                    <TabsTrigger
                      value="brand"
                      disabled={currentStep !== 'brand' && STEP_ORDER.indexOf(currentStep) > 1}
                      onClick={() => updateStep('brand')}
                      className="group relative rounded-lg transition-all duration-200 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-purple-700 py-2.5"
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <Building2
                          className="opacity-60 group-data-[state=active]:opacity-100 group-data-[state=active]:text-purple-600"
                          size={16}
                          strokeWidth={2}
                        />
                        {currentStep === 'brand' && <span className="hidden sm:inline font-medium">Brand</span>}
                        <Badge
                          className={cn(
                            "ml-1.5 transition-all",
                            currentStep === 'brand'
                              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0'
                              : 'bg-gray-100 text-gray-600'
                          )}
                        >
                          2
                        </Badge>
                      </div>
                    </TabsTrigger>
                    {!skipBilling && (
                      <TabsTrigger
                        value="plan"
                        disabled={currentStep !== 'plan' && STEP_ORDER.indexOf(currentStep) > 2}
                        onClick={() => updateStep('plan')}
                        className="group relative rounded-lg transition-all duration-200 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-purple-700 py-2.5"
                      >
                        <div className="flex items-center justify-center gap-1.5">
                          <CreditCard
                            className="opacity-60 group-data-[state=active]:opacity-100 group-data-[state=active]:text-purple-600"
                            size={16}
                            strokeWidth={2}
                          />
                          {currentStep === 'plan' && <span className="hidden sm:inline font-medium">Plan</span>}
                          <Badge
                            className={cn(
                              "ml-1.5 transition-all",
                              currentStep === 'plan'
                                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0'
                                : 'bg-gray-100 text-gray-600'
                            )}
                          >
                            3
                          </Badge>
                        </div>
                      </TabsTrigger>
                    )}
                    <TabsTrigger
                      value="success"
                      disabled={currentStep !== 'success'}
                      onClick={() => updateStep('success')}
                      className="group relative rounded-lg transition-all duration-200 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-purple-700 py-2.5"
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <CheckCircle2
                          className="opacity-60 group-data-[state=active]:opacity-100 group-data-[state=active]:text-purple-600"
                          size={16}
                          strokeWidth={2}
                        />
                        {currentStep === 'success' && <span className="hidden sm:inline font-medium">Start Creating</span>}
                        <Badge
                          className={cn(
                            "ml-1.5 transition-all",
                            currentStep === 'success'
                              ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white border-0'
                              : 'bg-gray-100 text-gray-600'
                          )}
                        >
                          {skipBilling ? '3' : '4'}
                        </Badge>
                      </div>
                    </TabsTrigger>
                  </TabsList>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>

                <div className="relative mt-6 mb-8">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-200 to-pink-200 rounded-full blur opacity-30" />
                  <Progress
                    value={skipBilling && currentStep !== 'plan'
                      ? STEP_PROGRESS_NO_BILLING[currentStep as 'help' | 'brand' | 'success']
                      : STEP_PROGRESS[currentStep]}
                    className="relative h-2 bg-gray-100"
                  />
                </div>

                <div className="mt-6">
                  <TabsContent value="help" className="animate-fadeIn">
                    <HelpWizardStep 
                      onComplete={() => handleStepComplete('help')} 
                      onBrandUpdate={handleBrandUpdate}
                      skipBilling={skipBilling}
                      existingBrandId={existingBrandId}
                    />
                  </TabsContent>

                  <TabsContent value="brand" className="animate-fadeIn">
                    <BrandWizardStep 
                      onComplete={() => handleStepComplete('brand')} 
                      onPrevious={() => updateStep('help')}
                      existingBrandId={existingBrandId || null}
                      onBrandUpdate={handleBrandUpdate}
                      skipBilling={skipBilling}
                    />
                  </TabsContent>

                  <TabsContent value="plan" className="animate-fadeIn">
                    <PlanSelector onComplete={() => handleStepComplete('plan')} />
                  </TabsContent>

                  <TabsContent value="success" className="animate-fadeIn">
                    <div className="text-center py-12">
                      <div className="relative inline-flex items-center justify-center mb-6">
                        <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full blur-xl opacity-40 animate-pulse" />
                        <div className="relative w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-xl">
                          <CheckCircle2 className="w-10 h-10 text-white" />
                        </div>
                      </div>
                      <h2 className="text-3xl font-bold mb-3 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                        Setup Complete!
                      </h2>
                      <p className="text-gray-600 mb-8 text-lg">Your brand is ready to start growing.</p>
                      <button
                        onClick={() => {
                          console.log('Success step button clicked. skipBilling:', skipBilling);
                          // Track onboarding completion
                          trackEvent(events.USER_ONBOARDING_COMPLETED, {
                            brand_id: brandData.id,
                            brand_name: brandData.brand_name,
                            time_to_complete: new Date().getTime() - (new Date(brandData.created_at || Date.now()).getTime()),
                            industry: brandData.brand_inspiration
                          });
                          
                          // Always update onboarding status when completing onboarding
                          const updateOnboardingStatus = async () => {
                            try {
                              const { data: { session } } = await supabase.auth.getSession();
                              if (!session) return;
                              
                              await supabase
                                .schema('marketing_and_customer_success')
                                .from('profiles')
                                .update({ onboarding_completed: true })
                                .eq('id', session.user.id);
                              
                              console.log('Onboarding completed flag set to true');
                              if (skipBilling) {
                                console.log('Redirecting to purchase success page');
                                navigate('/settings/purchase-success?skipBilling=true');
                              } else {
                                navigate('/dashboard');
                              }
                            } catch (error) {
                              console.error('Error updating onboarding status:', error);
                              // Still navigate even if the update fails
                              if (skipBilling) {
                                navigate('/settings/purchase-success?skipBilling=true');
                              } else {
                                navigate('/dashboard');
                              }
                            }
                          };
                          
                          updateOnboardingStatus();
                        }}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-3 rounded-xl font-semibold transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        <span className="flex items-center gap-2">
                          {skipBilling ? 'Continue to Brand Campaign' : 'Go to Dashboard'}
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </span>
                      </button>
                    </div>
                  </TabsContent>
                </div>
              </Tabs>
              </div>

              <style>{`
                @keyframes fadeIn {
                  from { opacity: 0; transform: translateY(10px); }
                  to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
              `}</style>
            </Card>
          </div>

          {/* Right side: Brand Preview - now visible on all screens */}
          <div>
            <BrandPreview 
              keyFields={keyFields}
              formValues={brandData}
              currentStep={0}
              getCurrentValue={(field) => {
                const value = brandData[field] as string || '';
                // DEBUG: Log field access
                if (field === 'brand_name' || field === 'elevator_pitch' || field === 'url') {
                  console.log(`[DEBUG BrandPreview] Getting field '${field}':`, value || '(empty)');
                }
                return value;
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
} 